import { useEffect, type ReactNode, type JSX } from 'react';
import { Provider as ReduxProvider, useDispatch } from 'react-redux';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { store, type AppDispatch } from '@app/store';
import { i18n } from '@app/providers/i18n';
import { LaunchDarklyProvider } from '@app/providers/LaunchDarklyProvider';
import { GoogleAnalytics } from '@app/providers/GoogleAnalytics';
import { SentryUser } from '@app/providers/SentryUser';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useTheme } from '@hooks/ui/useTheme';
import { setOnAuthFailure } from '@lib/baseQuery';
import { userSignedOut } from '@features/auth/slice';
import { tokenStore } from '@lib/tokenStore';
import { AuthBootstrap } from '@features/auth/AuthBootstrap';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Inner provider that consumes the theme preference and mirrors it onto
 * <html class="dark"> so Tailwind's class-based dark mode picks it up
 * everywhere — including Radix-portaled content (Dialog, Dropdown) that
 * mounts at document.body.
 */
function ThemedShell({ children }: { children: ReactNode }): JSX.Element {
  const { theme } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [theme]);

  return <>{children}</>;
}

/**
 * Wires the bare baseQuery into the store + router:
 * - onAuthFailure dispatches userSignedOut, clears tokens, and navigates
 * to /signin — no white screen, no stranded RTK Query subscriptions.
 * Mounted as a child of the Router so useNavigate() is available.
 */
function BaseQueryBridge({ children }: { children: ReactNode }): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    setOnAuthFailure(() => {
      tokenStore.clearAll();
      dispatch(userSignedOut());
      navigate('/signin', { replace: true });
    });
  }, [dispatch, navigate]);

  return <>{children}</>;
}

/**
 * Composition root for cross-cutting providers. Order:
 * ReduxProvider -> Router -> BaseQueryBridge -> I18n -> LD -> ThemedShell -> children
 */
export function AppProviders({ children }: AppProvidersProps): JSX.Element {
  return (
    <ReduxProvider store={store}>
      <BrowserRouter>
        <BaseQueryBridge>
          <I18nextProvider i18n={i18n}>
            <LaunchDarklyProvider>
              <TooltipProvider delayDuration={150}>
                <ThemedShell>
                  <AuthBootstrap>
                    <GoogleAnalytics />
                    <SentryUser />
                    {children}
                  </AuthBootstrap>
                </ThemedShell>
              </TooltipProvider>
            </LaunchDarklyProvider>
          </I18nextProvider>
        </BaseQueryBridge>
      </BrowserRouter>
    </ReduxProvider>
  );
}
